import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";
import { useBooking, scrollToSection } from "@/App";

const LINES = [
  { text: "Build a Healthier Body" },
  { text: "With the Right System —", italic: true },
  { text: "Not Another Extreme Diet." },
];

export default function Hero() {
  const { openBooking } = useBooking();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Indian woman practising mindful movement in calm natural light"
          className="w-full h-[115%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/75 via-ivory/35 to-ivory/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory/85 via-ivory/40 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-gold mb-7"
            data-testid="hero-eyebrow"
          >
            Health • Nutrition • Mind-Body Wellness
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[4.2rem] tracking-tight leading-[1.12] text-forest text-balance">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.italic ? "italic text-sage" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 text-lg sm:text-xl font-medium text-inkbody"
            data-testid="hero-subheadline"
          >
            Personalised health coaching designed around your body, your lifestyle and your health goals.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-4 text-base leading-relaxed text-inkmuted max-w-xl"
          >
            Your health is influenced by much more than what you eat. Nutrition, movement, sleep, stress,
            digestion, hormones, habits and your everyday environment are all connected. At Aarogyam Bharat,
            we help you build a personalised health system that fits your real life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.15 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={openBooking}
              data-testid="hero-book-discovery-call-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-4 text-sm font-bold tracking-wide shadow-hover hover:bg-botanical transition-colors duration-300"
            >
              BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToSection("programs")}
              data-testid="hero-explore-pathways-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-forest/25 text-forest px-8 py-4 text-sm font-bold tracking-wide hover:border-forest hover:bg-forest/5 transition-colors duration-300"
            >
              EXPLORE OUR HEALTH PATHWAYS <ArrowDown size={16} />
            </motion.button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="mt-9 text-xs uppercase tracking-[0.3em] font-bold text-inkmuted"
            data-testid="hero-trust-line"
          >
            Personalised • Holistic • Practical • Lifestyle-Focused
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
}
